import React, { useState, useEffect } from 'react';
import { seasonsService, SeasonWithReality } from '../../services/seasons.service';
import { realitiesService, Reality } from '../../services/realities.service';

export function SeasonsPage() {
  const [seasons, setSeasons] = useState<SeasonWithReality[]>([]);
  const [realities, setRealities] = useState<Reality[]>([]);
  const [selectedReality, setSelectedReality] = useState<string>('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingSeason, setEditingSeason] = useState<SeasonWithReality | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    reality_id: '',
    number: 1,
    year: new Date().getFullYear(),
    start_date: '',
    end_date: '',
    active: false,
  });

  useEffect(() => {
    loadRealities();
    loadSeasons();
  }, []);

  useEffect(() => {
    loadSeasons();
  }, [selectedReality]);

  const loadRealities = async () => {
    try {
      const data = await realitiesService.getAll();
      setRealities(data.filter(r => r.active));
    } catch (err: any) {
      setError(err.message || 'Error al cargar los realities');
    }
  };

  const loadSeasons = async () => {
    try {
      setLoading(true);
      let data;
      if (selectedReality === 'all') {
        data = await seasonsService.getAll();
      } else {
        data = await seasonsService.getByReality(selectedReality);
      }
      setSeasons(data);
    } catch (err: any) {
      setError(err.message || 'Error al cargar las temporadas');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = async (season?: SeasonWithReality) => {
    if (season) {
      setEditingSeason(season);
      setFormData({
        reality_id: season.reality_id,
        number: season.number,
        year: season.year,
        start_date: season.start_date || '',
        end_date: season.end_date || '',
        active: season.active ?? false,
      });
    } else {
      setEditingSeason(null);
      const nextNumber = selectedReality !== 'all'
        ? await seasonsService.getNextNumber(selectedReality)
        : 1;
      setFormData({
        reality_id: selectedReality !== 'all' ? selectedReality : realities[0]?.id || '',
        number: nextNumber,
        year: new Date().getFullYear(),
        start_date: '',
        end_date: '',
        active: false,
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingSeason(null);
    setFormData({
      reality_id: '',
      number: 1,
      year: new Date().getFullYear(),
      start_date: '',
      end_date: '',
      active: false,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validaciones
    if (!formData.reality_id) {
      setError('Debe seleccionar un reality');
      return;
    }

    if (formData.start_date && formData.end_date && formData.start_date > formData.end_date) {
      setError('La fecha de fin debe ser posterior a la fecha de inicio');
      return;
    }

    try {
      if (editingSeason) {
        await seasonsService.update(editingSeason.id, formData);
      } else {
        await seasonsService.create(formData);
      }
      await loadSeasons();
      handleCloseModal();
    } catch (err: any) {
      setError(err.message || 'Error al guardar la temporada');
    }
  };

  const handleToggleActive = async (id: string, currentActive: boolean) => {
    try {
      await seasonsService.toggleActive(id, !currentActive);
      await loadSeasons();
    } catch (err: any) {
      setError(err.message || 'Error al cambiar el estado');
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getDateStatus = (startDate: string | null, endDate: string | null) => {
    if (!startDate) return null;
    const now = new Date();
    const start = new Date(startDate);
    const end = endDate ? new Date(endDate) : null;

    if (now < start) return { text: 'Próxima', color: 'text-violet-600 dark:text-violet-400' };
    if (end && now > end) return { text: 'Finalizada', color: 'text-gray-600 dark:text-gray-400' };
    return { text: 'En curso', color: 'text-green-600 dark:text-green-400' };
  };

  if (loading && seasons.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  if (realities.length === 0) {
    return (
      <div className="bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-700 rounded-lg p-6 text-center">
        <h3 className="text-lg font-semibold text-yellow-800 dark:text-yellow-300 mb-2">
          No hay realities disponibles
        </h3>
        <p className="text-yellow-700 dark:text-yellow-400">
          Primero debes crear un reality antes de poder agregar temporadas.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Temporadas</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Gestiona las temporadas de los programas de reality
          </p>
        </div>
        <button
          onClick={() => handleOpenModal()}
          className="inline-flex items-center gap-2 px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          Agregar Temporada
        </button>
      </div>

      {/* Filter by Reality */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Filtrar por Reality
        </label>
        <select
          value={selectedReality}
          onChange={(e) => setSelectedReality(e.target.value)}
          className="w-full sm:w-auto px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
        >
          <option value="all">Todos los realities</option>
          {realities.map((reality) => (
            <option key={reality.id} value={reality.id}>
              {reality.name}
            </option>
          ))}
        </select>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* List - Mobile Cards / Desktop Table */}
      <div className="space-y-4 md:space-y-0">
        {seasons.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 text-center">
            <p className="text-gray-500 dark:text-gray-400">
              No hay temporadas registradas. Agrega una para comenzar.
            </p>
          </div>
        ) : (
          <>
            {/* Mobile Cards */}
            <div className="md:hidden space-y-4">
              {seasons.map((season) => {
                const dateStatus = getDateStatus(season.start_date, season.end_date);
                return (
                  <div
                    key={season.id}
                    className="bg-white dark:bg-gray-800 rounded-lg shadow p-4"
                  >
                    <div className="space-y-3">
                      <div>
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h3 className="font-medium text-gray-900 dark:text-white text-lg">
                              Temporada {season.number}
                            </h3>
                            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                              {season.reality?.name} • {season.year}
                            </p>
                          </div>
                          {dateStatus && (
                            <span className={`text-xs font-semibold ${dateStatus.color}`}>
                              {dateStatus.text}
                            </span>
                          )}
                        </div>
                      </div>

                      {(season.start_date || season.end_date) && (
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          {formatDate(season.start_date)} - {formatDate(season.end_date)}
                        </div>
                      )}

                      <div className="flex items-center gap-3 pt-2">
                        <button
                          onClick={() => handleToggleActive(season.id, season.active ?? false)}
                          className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
                            season.active
                              ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                              : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300'
                          }`}
                        >
                          {season.active ? 'Activa' : 'Inactiva'}
                        </button>
                        <button
                          onClick={() => handleOpenModal(season)}
                          className="text-violet-600 dark:text-violet-400 hover:text-violet-900 dark:hover:text-violet-300 text-sm font-medium"
                        >
                          Editar
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Desktop Table */}
            <div className="hidden md:block bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-900">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Reality
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Temporada
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Año
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Fechas
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Estado
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {seasons.map((season) => {
                    const dateStatus = getDateStatus(season.start_date, season.end_date);
                    return (
                      <tr key={season.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {season.reality?.name}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900 dark:text-white">
                            Temporada {season.number}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                          {season.year}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                          <div>{formatDate(season.start_date)} - {formatDate(season.end_date)}</div>
                          {dateStatus && (
                            <div className={`text-xs mt-1 ${dateStatus.color}`}>
                              {dateStatus.text}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <button
                            onClick={() => handleToggleActive(season.id, season.active ?? false)}
                            className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
                              season.active
                                ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                                : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300'
                            }`}
                          >
                            {season.active ? 'Activa' : 'Inactiva'}
                          </button>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button
                            onClick={() => handleOpenModal(season)}
                            className="text-violet-600 dark:text-violet-400 hover:text-violet-900 dark:hover:text-violet-300"
                          >
                            Editar
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            {/* Overlay */}
            <div
              className="fixed inset-0 bg-gray-500 dark:bg-gray-900 bg-opacity-75 dark:bg-opacity-75 transition-opacity"
              onClick={handleCloseModal}
            />

            {/* Modal Content */}
            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleSubmit}>
                <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                    {editingSeason ? 'Editar Temporada' : 'Nueva Temporada'}
                  </h3>

                  <div className="space-y-4">
                    {/* Reality */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Reality *
                      </label>
                      <select
                        required
                        value={formData.reality_id}
                        onChange={(e) => setFormData({ ...formData, reality_id: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                      >
                        <option value="">Seleccionar reality</option>
                        {realities.map((reality) => (
                          <option key={reality.id} value={reality.id}>
                            {reality.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Year */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Año *
                      </label>
                      <input
                        type="number"
                        required
                        min="2000"
                        max="2100"
                        value={formData.year}
                        onChange={(e) => setFormData({ ...formData, year: parseInt(e.target.value) })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      {/* Start Date */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Fecha de inicio
                        </label>
                        <input
                          type="date"
                          value={formData.start_date}
                          onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                        />
                      </div>

                      {/* End Date */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Fecha de fin
                        </label>
                        <input
                          type="date"
                          value={formData.end_date}
                          onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                        />
                      </div>
                    </div>

                    {/* Active */}
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="active"
                        checked={formData.active}
                        onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                        className="w-4 h-4 text-violet-600 rounded focus:ring-violet-500"
                      />
                      <label htmlFor="active" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Temporada activa
                      </label>
                    </div>
                    {formData.active && (
                      <p className="text-xs text-yellow-600 dark:text-yellow-400">
                        Al activar esta temporada, se desactivarán las demás del mismo reality.
                      </p>
                    )}
                  </div>
                </div>

                <div className="bg-gray-50 dark:bg-gray-900 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse gap-2">
                  <button
                    type="submit"
                    className="w-full sm:w-auto inline-flex justify-center rounded-lg border border-transparent shadow-sm px-4 py-2 bg-violet-600 text-base font-medium text-white hover:bg-violet-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-500"
                  >
                    {editingSeason ? 'Actualizar' : 'Crear'}
                  </button>
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="mt-3 w-full sm:mt-0 sm:w-auto inline-flex justify-center rounded-lg border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-800 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-500"
                  >
                    Cancelar
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
